export type ActiveRoute = {
    path: string
    pathParts: string[]
    params: Record<string, string | null>
    query: Record<string, string>
    previousPath: string | null
}

export type RouteFunction = (activeRoute: ActiveRoute, next: () => void) => void | Promise<void>

export type RouteEntry =
    | RouteFunction[]
    | {
          functions: RouteFunction[]
          has_children?: boolean
          disabled?: boolean
      }

export type RouteTable = Record<string, RouteEntry>

export type RouterOptions = {
    routes: RouteTable
    base?: string
    globalPreFunctions?: RouteFunction[]
    globalPostFunctions?: RouteFunction[]
    isSubRouter?: boolean
}

function getCurrentPath() {
    const pathname = window.location.pathname
    return pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname
}

const customNavigation = (event: Event, path: string) => {
    event.preventDefault()
    navigate(path)
}

export const sinlink = (element: HTMLAnchorElement) => {
    const path = element.getAttribute('href') || '/'
    element.addEventListener('click', (event) => customNavigation(event, path))
}

export const navigate = (path: string) => {
    history.pushState({}, '', path)
}

export default class Router {
    activeRoute: ActiveRoute | undefined
    isSubRouter = false
    history: string[] = []
    routes: RouteTable
    base: string
    globalPreFunctions: RouteFunction[]
    globalPostFunctions: RouteFunction[]
    private originalPushState: typeof history.pushState
    private originalReplaceState: typeof history.replaceState

    constructor({ routes, base = '', globalPreFunctions = [], globalPostFunctions = [], isSubRouter = false }: RouterOptions) {
        this.routes = routes
        this.base = base
        this.isSubRouter = isSubRouter
        this.globalPreFunctions = globalPreFunctions
        this.globalPostFunctions = globalPostFunctions

        this.handlePathChange = this.handlePathChange.bind(this)
        this.start()
    }

    start() {
        window.addEventListener('popstate', this.handlePathChange)
        window.addEventListener('hashchange', this.handlePathChange)

        this.originalPushState = history.pushState
        history.pushState = (...args: Parameters<typeof history.pushState>) => {
            this.originalPushState.apply(history, args)
            this.handlePathChange()
        }

        this.originalReplaceState = history.replaceState
        history.replaceState = (...args: Parameters<typeof history.replaceState>) => {
            this.originalReplaceState.apply(history, args)
            this.handlePathChange()
        }

        this.handlePathChange()
    }

    destroy() {
        window.removeEventListener('popstate', this.handlePathChange)
        window.removeEventListener('hashchange', this.handlePathChange)
        history.pushState = this.originalPushState
        history.replaceState = this.originalReplaceState
    }

    refresh() {
        this.handlePathChange()
    }

    handlePathChange() {
        this.matchRoute()
    }

    async matchRoute() {
        const path = getCurrentPath()
        const rawPathParts = path.split('/')

        let routeFunctions: RouteFunction[] | null = null
        let foundRoute: string | null = null
        let has_children = false

        for (const routePath in this.routes) {
            const routeParts = (this.base + routePath).split('/')

            if (
                rawPathParts.length !== routeParts.length &&
                !(this.routes[routePath] as { has_children?: boolean })?.has_children
            ) {
                continue
            }

            let match = true
            for (let i = 0; i < routeParts.length; i++) {
                const routeToCheck = routeParts[i]
                const currentRoute = rawPathParts[i]
                if (routeToCheck.startsWith(':') || routeToCheck === '*') continue
                if (routeToCheck === '**') break
                if (routeToCheck !== currentRoute) {
                    match = false
                    break
                }
            }
            if (match) {
                foundRoute = routePath
                const matchedRoute = this.routes[routePath]
                if (Array.isArray(matchedRoute)) {
                    has_children = false
                    routeFunctions = [...this.globalPreFunctions, ...matchedRoute, ...this.globalPostFunctions]
                } else {
                    if (matchedRoute.disabled) {
                        foundRoute = null
                    } else {
                        has_children = !!matchedRoute.has_children
                        routeFunctions = [...this.globalPreFunctions, ...matchedRoute.functions, ...this.globalPostFunctions]
                    }
                }
                break
            }
        }

        if (
            !foundRoute &&
            this.routes.__default &&
            (!this.isSubRouter || path.includes(this.base))
        ) {
            const defaultRoute = this.routes.__default
            if (Array.isArray(defaultRoute)) {
                routeFunctions = [...this.globalPreFunctions, ...defaultRoute, ...this.globalPostFunctions]
            } else {
                routeFunctions = [...this.globalPreFunctions, ...defaultRoute.functions, ...this.globalPostFunctions]
            }
            foundRoute = '__default'
        }

        let runFunctions = true
        if (has_children && this.history.length && this.history[this.history.length - 1] === path) {
            runFunctions = false
        }

        this.history.push(path)
        if (this.history.length > 2) this.history.shift()
        if (runFunctions && routeFunctions) {
            const params: Record<string, string | null> = {}
            let query: Record<string, string> = {}
            if (foundRoute) {
                const currentPathParts = path.split('/')
                const foundRouteParts = foundRoute.split('/')
                for (let i = 0; i < foundRouteParts.length; i++) {
                    const variableName = foundRouteParts[i]
                    if (variableName.startsWith(':')) {
                        params[variableName.replace(':', '')] = currentPathParts[i] || null
                    }
                }
            }

            const url = window.location.href
            if (url.includes('?')) {
                const urlParams = new URLSearchParams(url.substring(url.indexOf('?')))
                query = Object.fromEntries(urlParams.entries())
            }

            this.activeRoute = {
                path,
                pathParts: path.substring(1).split('/'),
                params,
                query,
                previousPath: this.history[this.history.length - 2] || null
            }
            try {
                for (const fn of routeFunctions) {
                    await this.runRouteFunction(fn)
                }
            } catch (e) {
                console.error('Router: guard threw, navigating to /generic-error', e)
                navigate('/generic-error')
            }
        }
    }

    // Terminal route functions (e.g. page loaders) typically don't call next(),
    // so globalPostFunctions will not execute after them. Use globalPreFunctions for guards.
    runRouteFunction(fn: RouteFunction): Promise<void> {
        return new Promise((resolve, reject) => {
            const next = () => resolve()
            const result = fn(this.activeRoute as ActiveRoute, next)
            if (result instanceof Promise) result.catch(reject)
        })
    }
}
